import { useEffect, useState } from "react";
import { api, fileUrl, dollars } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShoppingCart, RefreshCw, ShoppingBag } from "lucide-react";
import PrintfulMerchGrid from "@/components/PrintfulMerchGrid";

/**
 * /merch — three sections stacked:
 *   1. Official MeloCraft (site-branded, platform Printful) — everyone sees this
 *   2. This artist's merch (Printful) — only when ?album=… routes us to an artist's page
 *   3. Legacy album-cover mock catalog (kept as a fallback until every artist connects Printful)
 *
 * Admins get a small "Sync Platform Products" button so they can refresh the cache
 * after they design something new in Printful.
 */
export default function Merch() {
  const [items, setItems] = useState([]);
  const [params] = useSearchParams();
  const albumId = params.get("album");
  const { user } = useAuth();
  const nav = useNavigate();
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const url = albumId ? `/merch/for-album/${albumId}` : "/merch/all";
    api.get(url).then((r) => setItems(r.data.merch));
  }, [albumId]);

  const buyLegacy = async (item) => {
    if (!user) { toast.error("Sign in to buy merch"); nav("/login"); return; }
    try {
      const { data } = await api.post("/payments/checkout", {
        kind: "merch",
        item_id: `${item.variant_id}|${item.album_id}`,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); }
  };

  const syncPlatform = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/printful/platform/sync");
      toast.success(`Synced ${data.synced} platform product${data.synced === 1 ? "" : "s"}`);
      // Force the grid to re-fetch by reloading the page (simplest, one-time action).
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 pb-32">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#FF4500] mb-2">Merch</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter">Wear the record.</h1>
          <p className="font-mono text-sm text-gray-400 mt-3 max-w-2xl">
            Zero inventory. Every piece is printed on demand by <span className="text-white">Printful</span> and shipped direct.
            Artists route their orders to <span className="text-[#FF4500]">their own</span> Printful account — MeloCraft never touches the fulfillment.
          </p>
        </div>
        {isAdmin && (
          <Button
            data-testid="platform-sync-btn"
            onClick={syncPlatform}
            disabled={syncing}
            variant="outline"
            className="rounded-none border-[#2A2A2A] hover:border-[#FF4500] hover:text-[#FF4500] bg-transparent font-mono text-xs uppercase tracking-widest h-10 px-4"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} /> Sync platform products
          </Button>
        )}
      </div>

      {/* Official MeloCraft (platform) — always first */}
      <div className="mb-12">
        <PrintfulMerchGrid mode="platform" title="Official MeloCraft" hideIfEmpty={false} />
      </div>

      {/* Legacy album-cover catalog — falls back gracefully when no Printful yet */}
      {items.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <ShoppingBag className="h-5 w-5 text-[#FF4500]" />
            <h2 className="font-display text-2xl md:text-3xl">Album-cover merch</h2>
            <span className="font-mono text-[10px] uppercase tracking-widest text-gray-500 ml-2">
              Auto-generated for every album cover
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {items.map((m, i) => (
              <LegacyMerchCard key={`${m.variant_id}-${m.album_id}-${i}`} item={m} onBuy={buyLegacy} />
            ))}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center font-mono text-xs text-gray-500 uppercase py-16 border border-dashed border-[#2A2A2A]">
          No albums yet — merch appears once artists press records.
        </div>
      )}
    </div>
  );
}

function LegacyMerchCard({ item, onBuy }) {
  return (
    <div
      data-testid={`merch-card-${item.variant_id}-${item.album_id}`}
      className="border border-[#2A2A2A] bg-[#121212] hover-lift"
    >
      <div className="relative aspect-square overflow-hidden bg-neutral-900">
        <img src={item.base_image} alt={item.label} className="h-full w-full object-cover" />
        {item.cover_path && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="h-28 w-28 md:h-36 md:w-36 border-4 border-white/80 shadow-2xl overflow-hidden">
              <img src={fileUrl(item.cover_path)} alt={item.album_title} className="h-full w-full object-cover" />
            </div>
          </div>
        )}
        <div className="absolute bottom-2 left-2 bg-black/80 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
          {item.product}
        </div>
      </div>
      <div className="p-4">
        <div className="font-display font-bold truncate">{item.label}</div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 truncate mt-1">
          {item.album_title} · {item.artist_name}
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="font-mono text-sm text-[#FF4500]">{dollars(item.price_cents)}</span>
          <Button
            data-testid={`buy-merch-${item.variant_id}-${item.album_id}`}
            onClick={() => onBuy(item)}
            className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-xs uppercase tracking-widest h-8 px-3"
          >
            <ShoppingCart className="h-3 w-3 mr-1" /> Buy
          </Button>
        </div>
      </div>
    </div>
  );
}
